import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Flex, TextInput } from '@mantine/core';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import {
	FGAInternalRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { OrganizationRes } from '@dealstack/shared/lib/generated';
import { useOrganizations } from '@dealstack/frontend/app/pages/Organizations/hooks/useOrganizations';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { OrganizationsTable } from '@dealstack/frontend/app/pages/Organizations/components/OrganizationsTable/OrganizationsTable';
import { CreateOrganizationDrawer } from '@dealstack/frontend/app/pages/Organizations/components/CreateOrganizationDrawer/CreateOrganizationDrawer';
import { ImportOrganizationDrawer } from '@dealstack/frontend/app/pages/Organizations/components/ImportOrganizationDrawer/ImportOrganizationDrawer';
import { EditOrganizationDrawer } from '@dealstack/frontend/app/pages/Organizations/components/EditOrganizationDrawer/EditOrganizationDrawer';
import { DeleteOrganizationConfirm } from '@dealstack/frontend/app/pages/Organizations/components/DeleteOrganizationConfirm/DeleteOrganizationConfirm';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';

const SEARCH_DEBOUNCE_MS = 300;

export const Organizations = () => {
	const tOrganization = useTranslationOrganization();

	const { hasPermission: hasSupportPermission, isLoading: isLoadingPermission } =
		usePermissionRequest({
			resourceType: ResourceType.DEAL,
			resourceId: '',
			permissionRole: FGAInternalRole.DEALSTACK_SUPPORT,
		});

	const [createOpened, setCreateOpened] = useState(false);
	const [importOpened, setImportOpened] = useState(false);
	const [editTarget, setEditTarget] = useState<OrganizationRes | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<OrganizationRes | null>(null);
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounceValue(search, SEARCH_DEBOUNCE_MS);

	const dataOperations = useMantineTableDataOperations({ queryScope: [debouncedSearch] });
	const { pagination } = dataOperations;

	const { data, isFetching } = useOrganizations(
		{
			page: pagination.pageIndex + 1,
			limit: pagination.pageSize,
			search: debouncedSearch || undefined,
		},
		{ enabled: hasSupportPermission }
	);

	if (isLoadingPermission) {
		return <FullscreenLoader name="pageLoading" />;
	}

	if (!hasSupportPermission) {
		return <Navigate to="/dashboard" replace />;
	}

	return (
		<Flex direction="column" w="100%" p="1rem 2rem" gap="1rem">
			<PageHeader
				showQuickSwitch={false}
				pageTitle={tOrganization('organizations_management.title')}
			>
				<Button variant="outline" onClick={() => setImportOpened(true)}>
					{tOrganization('organizations_management.import_button')}
				</Button>
				<Button
					leftSection={<Icon variant="plus" size="md" color="inherit" />}
					onClick={() => setCreateOpened(true)}
				>
					{tOrganization('organizations_management.add_button')}
				</Button>
			</PageHeader>
			<TextInput
				w="20rem"
				data-testid="organizations-search"
				value={search}
				onChange={(event) => setSearch(event.currentTarget.value)}
				placeholder={tOrganization('organizations_management.search_placeholder')}
			/>
			<OrganizationsTable
				organizations={data?.rows ?? []}
				rowCount={data?.total ?? 0}
				dataOperations={dataOperations}
				isLoading={isFetching}
				onEdit={setEditTarget}
				onDelete={setDeleteTarget}
			/>
			<CreateOrganizationDrawer
				opened={createOpened}
				onClose={() => setCreateOpened(false)}
			/>
			<ImportOrganizationDrawer
				opened={importOpened}
				onClose={() => setImportOpened(false)}
			/>
			<EditOrganizationDrawer
				opened={!!editTarget}
				onClose={() => setEditTarget(null)}
				organization={editTarget}
			/>
			<DeleteOrganizationConfirm
				opened={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				organization={deleteTarget}
			/>
		</Flex>
	);
};
